<script setup lang="ts">
import { differenceInHours } from 'date-fns'

import InfluencerCardFooter from './card/footer.vue'
import InfluencerCardHeader from './card/header.vue'
import InfluencerCardInfo from './card/info.vue'
import InfluencerCardPillHolder from './card/pillHolder.vue'

import { useCuratorContestsInfos } from '~/composables/useCuratorContestsInfos'

import { useDraftStore } from '~/stores/draft'
import { useMiscSendtrackFiltersStore } from '~/stores/miscSendtrackFilters'
import { useTagStore } from '~/stores/tag'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { getDbTransaction } from '~/utils/IndexedDb/IndexedDb'

import type Tag from '~/entities/tag'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'
import type { SeoListsAsyncData } from '~/types/influencerListTagSeo'
import type { Locale } from '~/types/locale'

type Props = {
  influencer: Influencer | StatsV3Influencer
  seoPageUrlData?: SeoListsAsyncData['urlData']
}

type Emits = {
  click: [e: MouseEvent]
}

const props = withDefaults(defineProps<Props>(), {
  seoPageUrlData: () => [],
})
const emit = defineEmits<Emits>()

const { isContest } = useCuratorContestsInfos(props.influencer)
const { locale, t } = useI18n()
const route = useRoute()

const { BAND_DATA } = storeToRefs(useUserBandStore())
const { INFLUENCERS: CART } = storeToRefs(useDraftStore())
const { IS_LOGGED_IN } = storeToRefs(useUserStore())
const { id: DRAFT_ID } = storeToRefs(useDraftStore())
const { selected_tag_ids: SELECTED_FILTER_TAG_IDS } = storeToRefs(
  useMiscSendtrackFiltersStore(),
)

const { GET_TAGS_FROM_IDS, GET_TAGS_FROM_PARENT_ID } = useTagStore()
const { REMOVE_INF } = useDraftStore()

const priorityTagsIds = computed<number[]>(() => {
  const subgenreInSeoPageUrlData = props.seoPageUrlData.find(
    ({ tagTypeName }) => tagTypeName === 'subgenre',
  )

  // Since there can only be one subgenre in seoUrlData, there will never be a parent and a child with subgenre as it's type
  if (!subgenreInSeoPageUrlData) return SELECTED_FILTER_TAG_IDS.value

  if (!subgenreInSeoPageUrlData.isParent) {
    return [subgenreInSeoPageUrlData.tagId]
  } else {
    return (
      GET_TAGS_FROM_PARENT_ID(subgenreInSeoPageUrlData.tagId).map(
        ({ id }) => id,
      ) ?? []
    )
  }
})

const isAway = ref(false)
const pillContent = ref<string[]>([])

watch(priorityTagsIds, () => {
  computePillContent().then((newContent) => {
    pillContent.value = newContent
  })
})

const isSelected = computed<boolean>(() =>
  CART.value.includes(props.influencer.id),
)

async function checkIndexedDbCacheForPillContent(): Promise<string[] | null> {
  try {
    const TTL_IN_HOURS = 2
    const currentTimeStamp = Date.now()
    const transaction = await getDbTransaction('ComputeCache', 1)
    const store = transaction.objectStore(
      'influencerCardPillContentCache',
      'readwrite',
    )
    const cacheKey = createCachePillKey()

    const cachedValue = await store.get(cacheKey)
    if (!cachedValue) return null

    if (
      Math.abs(differenceInHours(currentTimeStamp, cachedValue.lastUpdated)) <
      TTL_IN_HOURS
    )
      return cachedValue.value

    store.delete(cacheKey)
    return null
  } catch (_) {
    return null
  }
}

function createCachePillKey() {
  return `${props.influencer.id}-${[...priorityTagsIds.value].sort(
    (a, b) => a - b,
  )}-${locale.value as Locale}` as const
}

async function cacheComputedPillContent(content: string[]): Promise<void> {
  try {
    const transaction = await getDbTransaction('ComputeCache', 1)
    const store = transaction.objectStore(
      'influencerCardPillContentCache',
      'readwrite',
    )

    store.put({
      influencerIdAndFilterIds: createCachePillKey(),
      lastUpdated: Date.now(),
      value: content,
    })
  } catch (_) {}
}

async function computePillContent(): Promise<string[]> {
  try {
    const content = await checkIndexedDbCacheForPillContent()

    if (content) return content
  } catch (_) {}
  const {
    tags: { liked, neutral },
  } = props.influencer
  const likedTags = liked?.subgenre ?? []
  const neutralTags = neutral?.subgenre ?? []
  const LIKED_TAGS: Tag[] = GET_TAGS_FROM_IDS(likedTags)
  const NEUTRAL_TAGS: Tag[] = GET_TAGS_FROM_IDS(neutralTags)
  const tagSortFn = (a: Tag, b: Tag) => (a.name < b.name ? -1 : 1)
  const priorityFilterTagIds = [...priorityTagsIds.value]
  const priorityBandSubgenresTagIds = [
    ...(BAND_DATA.value?.tags?.identity?.subgenre ?? []),
  ]
  const likedPriorityFilterTags = manageTagSet(
    LIKED_TAGS,
    tagSortFn,
    priorityFilterTagIds,
  )
  const neutralPriorityFilterTags = manageTagSet(
    NEUTRAL_TAGS,
    tagSortFn,
    priorityFilterTagIds,
  )
  const likedPriorityBandSubgenresTags = manageTagSet(
    LIKED_TAGS,
    tagSortFn,
    priorityBandSubgenresTagIds,
  )
  const neutralPriorityBandSubgenresTags = manageTagSet(
    NEUTRAL_TAGS,
    tagSortFn,
    priorityBandSubgenresTagIds,
  )
  const output = [
    ...new Set([
      ...likedPriorityFilterTags,
      ...neutralPriorityFilterTags,
      ...likedPriorityBandSubgenresTags,
      ...neutralPriorityBandSubgenresTags,
      ...LIKED_TAGS,
      ...NEUTRAL_TAGS,
    ]),
  ]
    .slice(0, 8)
    .map((tag: Tag) => t(`tags.subgenre.${tag.name}`) as string)
  cacheComputedPillContent(output)

  return output
}

function manageTagSet(
  baseSet: Tag[],
  sorter: (a: Tag, b: Tag) => number,
  filterIdSet: number[],
) {
  return baseSet.filter((tag) => filterIdSet.includes(tag.id)).sort(sorter)
}

function isOnSendtrack(): boolean {
  return route?.name?.toString().startsWith('draft-id-influencers___') ?? false
}

onMounted(() => {
  if (
    (isSelected.value &&
      (props.influencer as Influencer).internal_status === 'self_standby') ||
    !props.influencer.visible
  ) {
    isAway.value = true
    REMOVE_INF(props.influencer.id)
  }
  computePillContent().then((newContent) => {
    pillContent.value = newContent
  })
})
</script>

<template>
  <div
    class="tw-relative tw-flex tw-w-full tw-cursor-pointer tw-flex-col tw-rounded-xs tw-bg-white tw-shadow tw-transition-shadow hover:tw-shadow-hover"
    :data-test-id="'mainInfluencerCardContainer_' + influencer.id"
    :class="{ isContest }"
    @click="emit('click', $event)"
  >
    <NuxtLinkLocale
      :to="{
        path: `/influencer/profile/${influencer.slug}/`,
        query: {
          draftId: DRAFT_ID !== 0 ? DRAFT_ID.toString() : undefined,
          fromStep: isOnSendtrack() ? '1a' : undefined,
        },
      }"
      class="tw-flex tw-h-full tw-flex-col tw-justify-between tw-p-5"
    >
      <div class="tw-grid tw-h-full tw-grid-cols-1 tw-gap-3">
        <div class="tw-flex tw-flex-col tw-gap-3">
          <InfluencerCardHeader
            :key="influencer.id"
            :influencer="influencer"
            :selected="isSelected"
            :is-contest="isContest"
          />
          <InfluencerCardInfo
            :key="influencer.id"
            :influencer="influencer"
            :selected="isSelected"
          />
        </div>
        <Transition name="fade" mode="out-in">
          <InfluencerCardPillHolder
            :key="`${influencer.id}-${pillContent.length}`"
            :uniq-id="influencer.id"
            :content-array="pillContent"
            class="pillHolderWrapper"
            :class="{ isContest }"
          />
        </Transition>
      </div>
    </NuxtLinkLocale>
    <InfluencerCardFooter
      v-if="IS_LOGGED_IN"
      class="footer"
      :is-away="isAway"
      :is-selected="isSelected"
      :influencer="influencer"
      :is-contest="isContest"
    />
  </div>
</template>

<style scoped lang="scss">
.pillHolderWrapper {
  @apply tw-mb-auto tw-h-[58px] tw-items-start tw-justify-self-start tw-overflow-hidden;

  &.isContest {
    @apply tw-h-[21px];
  }
}

.footer {
  @apply tw-mt-auto;
}
</style>
